import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  AiOutlineHome,
  AiOutlineInfoCircle,
  AiOutlineShopping,
  AiOutlineSolution,
  AiOutlineMail,
} from "react-icons/ai";
import image from "../images/logo.png";
import { Link, useLocation } from "react-router-dom";

function Navbar() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isProductsMenuOpen, setIsProductsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  const productsRef = useRef(null);
  const location = useLocation();

  const isActive = (path) => location.pathname === path;
  const isProductActive = location.pathname.startsWith("/products");

  const toggleMenu = () => {
    setIsMenuOpen((prev) => !prev);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
    setIsProductsMenuOpen(false);
  };

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 12);
    };

    handleScroll();
    window.addEventListener("scroll", handleScroll);

    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (productsRef.current && !productsRef.current.contains(event.target)) {
        setIsProductsMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    closeMenu();
  }, [location.pathname]);

  const navItems = [
    {
      to: "/",
      label: "Home",
      icon: <AiOutlineHome />,
    },
    {
      to: "/about-us",
      label: "About Us",
      icon: <AiOutlineInfoCircle />,
    },
    {
      to: "/services",
      label: "Services",
      icon: <AiOutlineSolution />,
    },
  ];

  const productItems = [
    {
      to: "/products/water-meters",
      label: "Water Meters",
    },
    {
      to: "/products/gate-valve",
      label: "Gate Valve",
    },
  ];

  const desktopLinkClass = (path) =>
    `relative flex items-center gap-1.5 px-3 py-2 rounded-full transition-all duration-300 ${
      isActive(path)
        ? "text-white bg-[#0077b6] shadow-md"
        : "text-[#0077b6] hover:text-[#03045e] hover:bg-sky-50"
    }`;

  const mobileLinkClass = (path) =>
    `flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-300 ${
      isActive(path)
        ? "text-white bg-[#0077b6]"
        : "text-[#0077b6] hover:bg-sky-50 hover:text-[#03045e]"
    }`;

  return (
    <nav
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/90 backdrop-blur-md shadow-lg border-b border-sky-100"
          : "bg-white shadow-sm"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="flex justify-between items-center h-24">
          {/* Logo */}
          <Link to="/" onClick={closeMenu} className="flex items-center">
            <img
              src={image}
              className="w-auto h-20 object-contain"
              alt="SABEAN logo"
            />
          </Link>

          {/* Desktop Navigation */}
          <ul className="hidden md:flex items-center gap-2 font-serif font-semibold">
            {navItems.map((item) => (
              <li key={item.to}>
                <Link to={item.to} className={desktopLinkClass(item.to)}>
                  <span className="text-lg">{item.icon}</span>
                  {item.label}
                </Link>
              </li>
            ))}

            {/* Products Dropdown */}
            <li className="relative" ref={productsRef}>
              <button
                type="button"
                onClick={() => setIsProductsMenuOpen((prev) => !prev)}
                aria-expanded={isProductsMenuOpen}
                aria-haspopup="menu"
                className={`relative flex items-center gap-1.5 px-3 py-2 rounded-full transition-all duration-300 font-serif font-semibold ${
                  isProductActive
                    ? "text-white bg-[#0077b6] shadow-md"
                    : "text-[#0077b6] hover:text-[#03045e] hover:bg-sky-50"
                }`}
              >
                <AiOutlineShopping className="text-lg" />
                Products
                <svg
                  className={`w-3.5 h-3.5 ml-1 transition-transform duration-300 ${
                    isProductsMenuOpen ? "rotate-180" : ""
                  }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>

              <AnimatePresence>
                {isProductsMenuOpen && (
                  <motion.ul
                    initial={{ opacity: 0, y: 10, scale: 0.98 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 10, scale: 0.98 }}
                    transition={{ duration: 0.2 }}
                    className="absolute left-0 top-full mt-4 w-72 bg-white rounded-2xl shadow-2xl border border-sky-100 p-3"
                  >
                    {productItems.map((item) => (
                      <li key={item.to}>
                        <Link
                          to={item.to}
                          onClick={closeMenu}
                          className={`block px-4 py-3 rounded-xl transition-all duration-300 ${
                            location.pathname === item.to
                              ? "bg-[#0077b6] text-white"
                              : "text-gray-700 hover:bg-sky-50 hover:text-[#0077b6]"
                          }`}
                        >
                          {item.label}
                        </Link>
                      </li>
                    ))}
                  </motion.ul>
                )}
              </AnimatePresence>
            </li>

            <li>
              <Link
                to="/contact-us"
                className="ml-2 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#0077b6] text-white hover:bg-[#03045e] transition-all duration-300 shadow-md"
              >
                <AiOutlineMail />
                Contact Us
              </Link>
            </li>
          </ul>

          {/* Mobile Menu Button */}
          <button
            type="button"
            className="md:hidden inline-flex items-center justify-center w-11 h-11 rounded-xl text-[#0077b6] bg-sky-50 hover:bg-sky-100 transition"
            onClick={toggleMenu}
            aria-label="Toggle navigation menu"
            aria-expanded={isMenuOpen}
          >
            {isMenuOpen ? (
              <svg
                className="h-6 w-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            ) : (
              <svg
                className="h-6 w-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M4 6h16M4 12h16m-7 6h7"
                />
              </svg>
            )}
          </button>
        </div>
      </div>

      {/* Mobile Navigation */}
      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.25 }}
            className="md:hidden bg-white border-t border-sky-100 shadow-xl"
          >
            <div className="px-4 py-5 space-y-2 font-serif font-semibold">
              {navItems.map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  onClick={closeMenu}
                  className={mobileLinkClass(item.to)}
                >
                  <span className="text-xl">{item.icon}</span>
                  {item.label}
                </Link>
              ))}

              <div>
                <button
                  type="button"
                  onClick={() => setIsProductsMenuOpen((prev) => !prev)}
                  className={`w-full flex items-center justify-between px-4 py-3 rounded-xl transition-all duration-300 ${
                    isProductActive
                      ? "text-white bg-[#0077b6]"
                      : "text-[#0077b6] hover:bg-sky-50 hover:text-[#03045e]"
                  }`}
                >
                  <span className="flex items-center gap-3">
                    <AiOutlineShopping className="text-xl" />
                    Products
                  </span>

                  <svg
                    className={`w-4 h-4 transition-transform duration-300 ${
                      isProductsMenuOpen ? "rotate-180" : ""
                    }`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </button>

                <AnimatePresence>
                  {isProductsMenuOpen && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.2 }}
                      className="overflow-hidden"
                    >
                      <div className="ml-5 mt-2 space-y-2 border-l-2 border-sky-100 pl-4">
                        {productItems.map((item) => (
                          <Link
                            key={item.to}
                            to={item.to}
                            onClick={closeMenu}
                            className={`block px-4 py-2 rounded-lg text-sm transition-all duration-300 ${
                              location.pathname === item.to
                                ? "bg-sky-100 text-[#0077b6] font-bold"
                                : "text-gray-600 hover:bg-sky-50 hover:text-[#0077b6]"
                            }`}
                          >
                            {item.label}
                          </Link>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              <Link
                to="/contact-us"
                onClick={closeMenu}
                className="flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-[#0077b6] text-white hover:bg-[#03045e] transition-all duration-300 mt-4"
              >
                <AiOutlineMail />
                Contact Us
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
}

export default Navbar;